/**
 * Simple Authentication Context
 * Clean state management without complex race condition handling
 */

import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { sessionAuthService } from '../services/sessionAuth';
import { guestDataService } from '../services/guestDataService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [authStatus, setAuthStatus] = useState('loading'); // 'loading', 'authenticated', 'unauthenticated'
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const exchangeInProgressRef = useRef(false); // Prevent duplicate token exchanges

  /**
   * Check authentication status
   */
  const checkAuth = async () => {
    try {
      setIsLoading(true);
      console.log('🔍 Checking authentication status...');
      const { isAuthenticated, user: userData } = await sessionAuthService.checkAuth();
      
      if (isAuthenticated) {
        console.log('✅ User is authenticated');
        setUser(userData);
        setAuthStatus('authenticated');
      } else {
        console.log('❌ User is not authenticated');
        setUser(null);
        setAuthStatus('unauthenticated');
      }
    } catch (error) {
      console.error('❌ Auth check failed:', error);
      setUser(null);
      setAuthStatus('unauthenticated');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Initiate login
   */
  const login = async () => {
    try {
      await sessionAuthService.login();
    } catch (error) {
      console.error('❌ Login failed:', error);
      setAuthStatus('unauthenticated');
    }
  };

  /**
   * Initiate signup
   */
  const signup = async () => {
    try {
      await sessionAuthService.signup();
    } catch (error) {
      console.error('❌ Signup failed:', error);
      setAuthStatus('unauthenticated');
    }
  };

  /**
   * Sign out
   */
  const signOut = async () => {
    try {
      await sessionAuthService.signOut();
    } catch (error) {
      console.error('❌ Sign out failed:', error);
      // Fallback: clear local state
      setUser(null);
      setAuthStatus('unauthenticated');
    }
  };

  /**
   * Handle authentication callback (when user returns from Cognito)
   * Code is read from sessionStorage (set by index.js before React renders)
   */
  const handleAuthCallback = async (callbackData) => {
    const { code, state } = callbackData;
    
    if (!code) {
      console.error('❌ No code available in callback data');
      setAuthStatus('unauthenticated');
      exchangeInProgressRef.current = false;
      // Clear processing flag
      try {
        sessionStorage.removeItem('auth_callback_processing');
      } catch (e) {
        // Ignore
      }
      return;
    }

    console.log('🔄 Processing authentication callback with code:', code.substring(0, 20) + '...');
    
    try {
      // Exchange code for tokens
      console.log('📡 Calling exchangeCodeForTokens...');
      const exchangeResult = await sessionAuthService.exchangeCodeForTokens(code, state);
      console.log('📥 Received exchange result:', {
        hasResult: !!exchangeResult,
        hasSuccess: !!exchangeResult?.success,
        hasUser: !!exchangeResult?.user,
        keys: exchangeResult ? Object.keys(exchangeResult) : []
      });
      
      // Use the user data from the token exchange response
      if (exchangeResult && exchangeResult.success && exchangeResult.user) {
        console.log('✅ Token exchange successful, setting user data:', exchangeResult.user);
        setUser(exchangeResult.user);
        setAuthStatus('authenticated');
        setIsLoading(false);
        
        // Migrate guest data to server (if any exists)
        guestDataService.migrateGuestDataToServer(exchangeResult.user)
          .then(success => {
            if (success) {
              console.log('✅ Guest data migration completed');
            } else {
              console.warn('⚠️ Guest data migration had issues, but continuing');
            }
          })
          .catch(error => {
            console.error('❌ Error during guest data migration:', error);
            // Don't block login if migration fails
          });
        
        // Store user data in sessionStorage to persist across redirect
        try {
          sessionStorage.setItem('auth_user_data', JSON.stringify(exchangeResult.user));
          sessionStorage.setItem('auth_just_exchanged', 'true');
          console.log('💾 Stored auth data in sessionStorage for redirect');
        } catch (e) {
          console.warn('⚠️ Could not store auth data in sessionStorage:', e);
        }
        
        // Clear the callback code and processing flag from sessionStorage
        try {
          sessionStorage.removeItem('auth_callback_code');
          sessionStorage.removeItem('auth_callback_processing');
        } catch (e) {
          // Ignore
        }
        
        // Delay to ensure cookies are fully set before redirect
        setTimeout(() => {
          console.log('🔄 Redirecting to home page...');
          window.location.href = '/';
        }, 300);
      } else {
        // Response format unexpected (success but no user) - avoid broken auth state
        console.warn('⚠️ Token exchange response missing user data:', exchangeResult ? Object.keys(exchangeResult) : 'null');
        setAuthStatus('unauthenticated');
        setUser(null);
        setIsLoading(false);
        try {
          sessionStorage.removeItem('auth_callback_code');
          sessionStorage.removeItem('auth_callback_processing');
        } catch (e) {
          // Ignore
        }
        setTimeout(() => {
          window.location.href = '/';
        }, 300);
      }
    } catch (exchangeError) {
      console.error('❌ Token exchange failed:', exchangeError);
      console.error('❌ Error details:', {
        message: exchangeError?.message,
        stack: exchangeError?.stack,
        name: exchangeError?.name
      });
      setAuthStatus('unauthenticated');
      setIsLoading(false);
      // Clear the callback code and processing flag on error so user can retry
      try {
        sessionStorage.removeItem('auth_callback_code');
        sessionStorage.removeItem('auth_callback_processing');
      } catch (e) {
        // Ignore
      }
      // Redirect to home on error
      setTimeout(() => {
        window.location.href = '/';
      }, 1000);
    } finally {
      // Reset the ref after a delay to allow redirect to complete
      setTimeout(() => {
        exchangeInProgressRef.current = false;
      }, 1000);
    }
  };

  // Single useEffect to handle both callback processing and auth checking
  // This prevents parallel execution and race conditions
  useEffect(() => {
    const pathname = window.location.pathname;
    
    // PRIORITY 1: Handle callback route first (if we're on it)
    if (pathname === '/auth/callback') {
      console.log('📍 On callback route - processing callback');
      
      // Read callback data from sessionStorage (set by index.js before React renders)
      let callbackData = null;
      try {
        const stored = sessionStorage.getItem('auth_callback_code');
        if (stored) {
          callbackData = JSON.parse(stored);
          // Check if this code is too old (more than 5 minutes) - codes expire quickly
          const age = Date.now() - (callbackData.timestamp || 0);
          if (age > 5 * 60 * 1000) {
            console.warn('⚠️ Callback code is too old, ignoring:', age, 'ms');
            sessionStorage.removeItem('auth_callback_code');
            callbackData = null;
          }
        }
      } catch (e) {
        console.error('❌ Failed to read callback data from sessionStorage:', e);
      }
      
      // Check for OAuth errors
      let error = null;
      try {
        error = sessionStorage.getItem('auth_callback_error');
        if (error) {
          sessionStorage.removeItem('auth_callback_error');
        }
      } catch (e) {
        // Ignore
      }
      
      // CRITICAL: Check if we're already processing this code (prevents React Strict Mode duplicate)
      let isProcessing = false;
      try {
        isProcessing = sessionStorage.getItem('auth_callback_processing') === 'true';
      } catch (e) {
        // Ignore
      }
      
      console.log('🔍 Callback route check:', { 
        hasCallbackData: !!callbackData,
        hasError: !!error,
        isProcessing
      });
      
      // If already processing, skip (React Strict Mode protection)
      if (isProcessing) {
        console.log('⏸️ Callback already being processed, skipping duplicate');
        return;
      }
      
      // Handle OAuth errors
      if (error) {
        console.error('❌ OAuth error:', error);
        localStorage.removeItem('pkce_code_verifier');
        localStorage.removeItem('pkce_state');
        localStorage.removeItem('pkce_timestamp');
        sessionStorage.removeItem('auth_callback_code');
        sessionStorage.removeItem('auth_callback_processing');
        window.location.href = '/?error=' + encodeURIComponent(error);
        return;
      }
      
      // No callback data at all
      if (!callbackData || !callbackData.code) {
        console.error('❌ No authorization code found in sessionStorage');
        localStorage.removeItem('pkce_code_verifier');
        localStorage.removeItem('pkce_state');
        localStorage.removeItem('pkce_timestamp');
        sessionStorage.removeItem('auth_callback_processing');
        window.location.href = '/';
        return;
      }
      
      // Mark as processing in sessionStorage (atomic operation to prevent duplicates)
      try {
        sessionStorage.setItem('auth_callback_processing', 'true');
      } catch (e) {
        console.error('❌ Failed to set processing flag:', e);
      }
      
      exchangeInProgressRef.current = true;
      
      // Process the callback - this will handle redirect
      handleAuthCallback(callbackData);
      return; // Exit early - don't run checkAuth
    }
    
    // PRIORITY 2: Check if we just completed a token exchange (after redirect from callback)
    let shouldSkipCheckAuth = false;
    
    try {
      const justExchanged = sessionStorage.getItem('auth_just_exchanged');
      const storedUserData = sessionStorage.getItem('auth_user_data');
      
      console.log('🔍 Checking sessionStorage on mount:', {
        justExchanged,
        hasStoredData: !!storedUserData,
        timestamp: new Date().toISOString()
      });
      
      if (justExchanged === 'true' && storedUserData) {
        console.log('⏭️ Just completed token exchange, using stored user data - SKIPPING checkAuth');
        shouldSkipCheckAuth = true;
        
        try {
          const userData = JSON.parse(storedUserData);
          console.log('✅ Setting user from stored data:', { id: userData.id, email: userData.email });
          setUser(userData);
          setAuthStatus('authenticated');
          setIsLoading(false);
          
          // Migrate guest data to server (if any exists) - only once
          const migrationDone = sessionStorage.getItem('guest_data_migrated');
          if (!migrationDone) {
            guestDataService.migrateGuestDataToServer(userData)
              .then(success => {
                if (success) {
                  console.log('✅ Guest data migration completed');
                  sessionStorage.setItem('guest_data_migrated', 'true');
                } else {
                  console.warn('⚠️ Guest data migration had issues, but continuing');
                }
              })
              .catch(error => {
                console.error('❌ Error during guest data migration:', error);
                // Don't block login if migration fails
              });
          }
          
          // Clear the flag immediately so we don't process it again
          sessionStorage.removeItem('auth_just_exchanged');
          sessionStorage.removeItem('auth_user_data');
          console.log('🧹 Cleared sessionStorage flags');
          
          // Wait before background check to ensure cookies are fully set
          setTimeout(async () => {
            try {
              console.log('🔄 Running background auth verification (cookies should be ready now)...');
              const response = await fetch(`${sessionAuthService.baseUrl}/api/auth/me`, {
                method: 'GET',
                credentials: 'include'
              });
              
              if (response.ok) {
                const freshUserData = await response.json();
                console.log('✅ Background auth check successful, cookies are working');
                setUser(freshUserData);
              } else {
                const errorText = await response.text().catch(() => '');
                console.warn('⚠️ Background auth check returned', response.status, errorText, '- but keeping stored user data');
              }
            } catch (err) {
              console.warn('⚠️ Background auth check failed:', err);
            }
          }, 2000);
        } catch (e) {
          console.error('❌ Could not parse stored user data:', e);
          shouldSkipCheckAuth = false;
        }
      }
    } catch (e) {
      console.warn('⚠️ Could not access sessionStorage:', e);
    }
    
    // PRIORITY 3: Normal auth check (only if not on callback and didn't just exchange)
    if (!shouldSkipCheckAuth) {
      console.log('🔄 Running normal checkAuth...');
      checkAuth();
    } else {
      console.log('⏭️ Skipped normal checkAuth - using stored token exchange data');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  const value = {
    authStatus,
    user,
    isLoading,
    login,
    signup,
    signOut,
    checkAuth,
    refreshUser: checkAuth // Alias for refreshing user data
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
